<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue'
import { ChevronDown, ChevronUp, RotateCcw, Search } from '@lucide/vue'
import { RrButton, type ButtonSize } from '@xjlv2/ui'

const props = withDefaults(
  defineProps<{
    label?: string
    result?: string
    expandable?: boolean
    expanded?: boolean
    defaultExpanded?: boolean
    collapsed?: boolean
    loading?: boolean
    searchButtonText?: string
    resetButtonText?: string
    showSearch?: boolean
    showReset?: boolean
    searchVisible?: boolean
    size?: ButtonSize
  }>(),
  {
    label: '',
    result: '',
    expandable: false,
    expanded: undefined,
    defaultExpanded: false,
    collapsed: undefined,
    loading: false,
    searchButtonText: '查询',
    resetButtonText: '重置',
    showSearch: true,
    showReset: true,
    searchVisible: true,
    size: 'middle'
  }
)

const emit = defineEmits<{
  submit: []
  reset: []
  'update:expanded': [expanded: boolean]
  'update:collapsed': [collapsed: boolean]
  'update:searchVisible': [visible: boolean]
  'toggle-expand': [expanded: boolean]
}>()

const slots = useSlots()
const hasHeading = computed(() => Boolean(props.label || props.result || slots.heading))

const localExpanded = ref(props.defaultExpanded)
const isExpanded = computed(() => {
  if (props.collapsed !== undefined) return !props.collapsed
  if (props.expanded !== undefined) return props.expanded
  return localExpanded.value
})

watch(
  () => props.expanded,
  (val) => {
    if (val !== undefined) localExpanded.value = val
  }
)

watch(
  () => props.collapsed,
  (val) => {
    if (val !== undefined) localExpanded.value = !val
  }
)

function toggleExpand() {
  const next = !isExpanded.value
  localExpanded.value = next
  emit('update:expanded', next)
  emit('update:collapsed', !next)
  emit('toggle-expand', next)
}

function handleReset() {
  emit('reset')
}

function handleSubmit() {
  emit('submit')
}
</script>

<template>
  <form
    v-show="searchVisible"
    class="rr-filter-bar"
    :class="[
      `rr-filter-bar--${size}`,
      {
        'is-expandable': expandable,
        'is-expanded': isExpanded,
        'has-heading': hasHeading
      }
    ]"
    @submit.prevent="handleSubmit"
  >
    <div class="rr-filter-bar__main">
      <div v-if="hasHeading" class="rr-filter-bar__heading">
        <slot name="heading">
          <strong v-if="label">{{ label }}</strong>
          <span v-if="result">{{ result }}</span>
        </slot>
      </div>

      <div class="rr-filter-bar__fields">
        <slot />
      </div>

      <div class="rr-filter-bar__actions">
        <slot name="actions">
          <RrButton
            v-if="showSearch"
            html-type="submit"
            type="primary"
            :size="size"
            :loading="loading"
            class="rr-filter-bar__submit-btn"
          >
            <template #icon>
              <Search :size="size === 'small' ? 12 : 14" aria-hidden="true" />
            </template>
            {{ searchButtonText }}
          </RrButton>
          <RrButton
            v-if="showReset"
            type="default"
            :size="size"
            :disabled="loading"
            class="rr-filter-bar__reset-btn"
            @click="handleReset"
          >
            <template #icon>
              <RotateCcw :size="size === 'small' ? 12 : 14" aria-hidden="true" />
            </template>
            {{ resetButtonText }}
          </RrButton>
          <button
            v-if="expandable"
            type="button"
            class="rr-filter-bar__expand-btn"
            :aria-expanded="isExpanded"
            @click="toggleExpand"
          >
            <span>{{ isExpanded ? '收起' : '展开' }}</span>
            <ChevronUp v-if="isExpanded" :size="14" aria-hidden="true" />
            <ChevronDown v-else :size="14" aria-hidden="true" />
          </button>
        </slot>
      </div>
    </div>

    <div
      v-if="expandable && ($slots.extra || $slots.more)"
      class="rr-filter-bar__extra-wrapper"
      :class="{ 'is-open': isExpanded }"
    >
      <div class="rr-filter-bar__extra">
        <slot name="extra" />
        <slot name="more" />
      </div>
    </div>
  </form>
</template>

<style scoped>
.rr-filter-bar {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-3);
  min-width: 0;
  margin-bottom: var(--rr-space-4);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  background: var(--rr-color-surface);
  box-shadow: var(--rr-shadow-panel);
  padding: 12px 16px;
}

.rr-filter-bar__main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.rr-filter-bar__heading {
  display: grid;
  align-self: center;
  min-width: 92px;
  gap: 3px;
  border-right: 1px solid var(--rr-color-border);
  padding-right: 14px;
}

.rr-filter-bar__heading strong {
  color: var(--rr-color-text-strong);
  font-size: 13px;
}

.rr-filter-bar__heading span {
  color: var(--rr-color-text-muted);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}

.rr-filter-bar__fields {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
}

/* 统一 FilterBar 内部直接或间接表单控件样式，对齐 Ant Design Pro QueryFilter */
.rr-filter-bar__fields :deep(input:not(.rr-input):not([type=checkbox]):not([type=radio])),
.rr-filter-bar__fields :deep(select),
.rr-filter-bar__fields :deep(.rr-input-group-wrapper),
.rr-filter-bar__fields :deep(.rr-input-wrapper),
.rr-filter-bar__fields :deep(.rr-select) {
  width: 220px !important;
  max-width: 100%;
  height: 32px !important;
  min-height: 32px !important;
  box-sizing: border-box !important;
  font-size: 13px;
}

.rr-filter-bar__fields :deep(input:not(.rr-input):not([type=checkbox]):not([type=radio])) {
  padding: 0 10px;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-surface);
  color: var(--rr-color-text);
  outline: 0;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.rr-filter-bar__fields :deep(input:not(.rr-input):not([type=checkbox]):not([type=radio]):hover) {
  border-color: var(--rr-color-primary-border);
}

.rr-filter-bar__fields :deep(input:not(.rr-input):not([type=checkbox]):not([type=radio]):focus) {
  border-color: var(--rr-color-primary);
  box-shadow: 0 0 0 2px var(--rr-color-primary-soft);
}

.rr-filter-bar__fields :deep(select) {
  width: 140px !important;
  padding: 0 8px;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-surface);
  color: var(--rr-color-text);
  outline: 0;
  cursor: pointer;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.rr-filter-bar__fields :deep(select:hover) {
  border-color: var(--rr-color-primary-border);
}

.rr-filter-bar__fields :deep(select:focus) {
  border-color: var(--rr-color-primary);
  box-shadow: 0 0 0 2px var(--rr-color-primary-soft);
}

.rr-filter-bar__fields :deep(label) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--rr-color-text);
  white-space: nowrap;
}

.rr-filter-bar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  white-space: nowrap;
}

.rr-filter-bar__expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  min-height: 32px;
  border: 1px solid transparent;
  border-radius: var(--rr-radius-control);
  background: transparent;
  color: var(--rr-color-primary);
  padding: 0 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color var(--rr-motion-duration-fast) var(--rr-motion-ease-out),
    color var(--rr-motion-duration-fast) var(--rr-motion-ease-out);
}

.rr-filter-bar__expand-btn:hover {
  background: var(--rr-color-primary-soft);
}

.rr-filter-bar__extra-wrapper {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition: grid-template-rows 200ms ease, opacity 180ms ease;
}

.rr-filter-bar__extra-wrapper.is-open {
  grid-template-rows: 1fr;
  opacity: 1;
}

.rr-filter-bar__extra {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  align-items: end;
  gap: 10px;
  min-width: 0;
  overflow: hidden;
  border-top: 1px dashed var(--rr-color-border);
  padding-top: var(--rr-space-3);
}

@media (max-width: 980px) {
  .rr-filter-bar__main,
  .rr-filter-bar.has-heading .rr-filter-bar__main {
    align-items: stretch;
  }

  .rr-filter-bar__heading {
    justify-content: start;
    border-right: 0;
    padding-right: 0;
  }
}

@media (max-width: 768px) {
  .rr-filter-bar {
    padding: 10px 12px;
  }

  .rr-filter-bar__fields {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .rr-filter-bar__actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (prefers-reduced-motion: reduce) {
  .rr-filter-bar__extra-wrapper {
    transition: none;
  }
}
</style>
